/**
 * Canonical billing-mode display for the customer app.
 *
 * Prefers the server-provided `billingModeDisplay` (added by sw-ev-api
 * `services/sessions/paymentDisplay.js:resolveBillingModeDisplay`) so
 * labels are guaranteed to match the admin UI. Falls back to a local
 * map for older API responses.
 *
 * DB values are unchanged; this module only formats.
 */

const FALLBACK = Object.freeze({
  PUBLIC_CARD:     { label: 'Public card',           category: 'card',    paymentRequired: true  },
  DISCOUNTED_CARD: { label: 'Discounted card',       category: 'card',    paymentRequired: true  },
  FLEET_INVOICE:   { label: 'Account billed',        category: 'account', paymentRequired: false },
  FREE:            { label: 'Complimentary',         category: 'free',    paymentRequired: false },
  CREDIT:          { label: 'Reimbursement covered', category: 'credit',  paymentRequired: false },
});

const UNKNOWN = Object.freeze({
  label: 'Not assigned',
  category: 'unknown',
  paymentRequired: true,
});

export const CANONICAL_BILLING_MODES = Object.freeze([
  'PUBLIC_CARD',
  'DISCOUNTED_CARD',
  'FLEET_INVOICE',
  'FREE',
  'CREDIT',
]);

export function displayForBillingMode(value) {
  const key = typeof value === 'string' && value.length ? value : null;
  const entry = key ? FALLBACK[key] : null;
  return entry ? { value: key, ...entry } : { value: key, ...UNKNOWN };
}

/**
 * Accepts a session-shaped input (pricing.billingModeDisplay,
 * pricing.billingMode, billing_mode, or a raw string) and returns the
 * canonical display object.
 */
export function billingModeDisplayFor(input) {
  if (input == null) return { value: null, ...UNKNOWN };
  if (typeof input === 'string') return displayForBillingMode(input);
  if (typeof input !== 'object') return { value: null, ...UNKNOWN };

  // Session DTO: pricing.billingModeDisplay
  const nested = input.pricing?.billingModeDisplay;
  const flatCamel = input.billingModeDisplay;
  const flatSnake = input.billing_mode_display;
  const serverProvided = nested || flatCamel || flatSnake;

  if (
    serverProvided
    && typeof serverProvided === 'object'
    && typeof serverProvided.label === 'string'
  ) {
    return {
      value:
        serverProvided.value
        ?? input.pricing?.billingMode
        ?? input.billingMode
        ?? input.billing_mode
        ?? null,
      label: serverProvided.label,
      category: serverProvided.category ?? 'unknown',
      paymentRequired:
        typeof serverProvided.paymentRequired === 'boolean'
          ? serverProvided.paymentRequired
          : true,
    };
  }

  const raw =
    input.pricing?.billingMode
    ?? input.billingMode
    ?? input.billing_mode
    ?? null;
  return displayForBillingMode(raw);
}

export function billingModeLabel(value) {
  return displayForBillingMode(value).label;
}
